import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../apiBase';

const API = getApiBaseUrl();

function adminApi(token) {
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });
}

async function downloadFile(token, path, filename) {
    const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function AdminBillingSchoolsTab({ token }) {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr('');
            try {
                const res = await adminApi(token).get('/admin/reports/schools-billing', {
                    params: { status, sortBy }
                });
                if (!cancelled) setRows(res.data || []);
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.message || 'Ошибка загрузки');
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [token, status, sortBy]);

    const totals = useMemo(() => ({
        students: rows.reduce((s, r) => s + (r.currentActiveStudents || 0), 0),
        extra: rows.reduce((s, r) => s + (r.extraStudents || 0), 0),
        base: rows.reduce((s, r) => s + (r.baseMonthlyPrice || 0), 0),
        total: rows.reduce((s, r) => s + (r.estimatedTotal || 0), 0)
    }), [rows]);

    async function handleDownload() {
        try {
            await downloadFile(token, '/admin/reports/export/schools-billing.xlsx', `schools-billing-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch {
            setErr('Не удалось скачать Excel');
        }
    }

    async function handleEmail() {
        setMsg('');
        try {
            const res = await adminApi(token).post('/admin/reports/send-email', {
                reportType: 'schools-billing'
            });
            setMsg(res.data?.message || 'Отправлено');
        } catch (e) {
            setErr(e.response?.data?.message || 'Ошибка отправки');
        }
    }

    function toggleSort(field) {
        setSortBy(field);
    }

    const th = (label, field) => (
        <th
            style={{ padding: 8, cursor: field ? 'pointer' : 'default', userSelect: 'none' }}
            onClick={() => field && toggleSort(field)}
        >
            {label}{sortBy === field ? ' ▼' : ''}
        </th>
    );

    const fmt = (n) => (n ?? 0).toLocaleString('ru-RU');

    return (
        <div>
            <div className="sp-panel sp-toolbar" style={{ marginBottom: 16, padding: 14, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <label style={{ fontSize: 12, color: '#475569' }}>
                    Статус{' '}
                    <select value={status} onChange={e => setStatus(e.target.value)} style={{ marginLeft: 6, padding: '6px 8px', borderRadius: 8 }}>
                        <option value="all">Все</option>
                        <option value="active">Активные</option>
                        <option value="trial">Trial</option>
                        <option value="suspended">Suspended</option>
                    </select>
                </label>
                <button type="button" className="sp-btn sp-btn--secondary" onClick={handleDownload}>📥 Скачать Excel</button>
                <button type="button" className="sp-btn sp-btn--primary" onClick={handleEmail}>📧 Отправить на email</button>
            </div>

            {loading && <div style={{ padding: 20, color: '#64748b' }}>Загрузка…</div>}
            {err && <div style={{ padding: 12, marginBottom: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 8 }}>{err}</div>}
            {msg && <div style={{ padding: 12, marginBottom: 12, background: '#ecfdf5', color: '#065f46', borderRadius: 8 }}>{msg}</div>}

            {!loading && (
                <div className="sp-panel" style={{ overflowX: 'auto', padding: 0 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left', background: '#f8fafc' }}>
                                {th('Школа', 'name')}
                                <th style={{ padding: 8 }}>Владелец</th>
                                <th style={{ padding: 8 }}>Тариф</th>
                                {th('Активных', 'students')}
                                {th('Итого/мес', 'total')}
                                <th style={{ padding: 8 }}>Статус</th>
                                {th('След. платёж', 'nextBilling')}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.schoolId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: 8 }}>{r.schoolName}</td>
                                    <td style={{ padding: 8 }}>{r.ownerName}</td>
                                    <td style={{ padding: 8 }}>{r.planName}</td>
                                    <td style={{ padding: 8 }}>{r.currentActiveStudents} (+{r.extraStudents})</td>
                                    <td style={{ padding: 8, fontWeight: 600 }}>{fmt(r.estimatedTotal)} ₽</td>
                                    <td style={{ padding: 8 }}>{r.subscriptionStatus}</td>
                                    <td style={{ padding: 8 }}>{r.nextBillingDate ? new Date(r.nextBillingDate).toLocaleDateString('ru-RU') : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{ fontWeight: 800, background: '#fef2f2' }}>
                                <td style={{ padding: 8 }} colSpan={3}>ИТОГО</td>
                                <td style={{ padding: 8 }}>{totals.students} (+{totals.extra})</td>
                                <td style={{ padding: 8 }}>{fmt(totals.total)} ₽</td>
                                <td style={{ padding: 8 }} colSpan={2} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
